import { X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import type { Product } from '../types';

type Props = { product: Product; onClose: () => void };
type MeasureKey = 'busto' | 'cintura' | 'quadril' | 'comprimento';

const sizeNames: Record<string, string> = { PP: 'XS', P: 'S', M: 'M', G: 'L', GG: 'XL', XGG: 'XXL' };
const bodyMeasures: Record<string, Record<MeasureKey, number>> = {
  PP: { busto: 80, cintura: 62, quadril: 90, comprimento: 56 },
  P: { busto: 86, cintura: 68, quadril: 96, comprimento: 58 },
  M: { busto: 92, cintura: 74, quadril: 102, comprimento: 60 },
  G: { busto: 98, cintura: 80, quadril: 108, comprimento: 62 },
  GG: { busto: 104, cintura: 86, quadril: 114, comprimento: 64 },
  XGG: { busto: 110, cintura: 92, quadril: 120, comprimento: 66 },
};
const labels: Record<MeasureKey, string> = { busto: 'Busto', cintura: 'Cintura', quadril: 'Quadril', comprimento: 'Comprimento' };

function columnsFor(product: Product): MeasureKey[] {
  if (product.category === 'leggings-calcas' || product.category === 'shorts-bermudas') return ['cintura', 'quadril', 'comprimento'];
  if (product.category === 'tops' || product.category === 'camisetas' || product.category === 'jaquetas') return ['busto', 'cintura', 'comprimento'];
  return ['busto', 'cintura', 'quadril', 'comprimento'];
}

export function SizeGuideModal({ product, onClose }: Props) {
  const [unit, setUnit] = useState<'cm' | 'in'>('cm');
  const columns = useMemo(() => columnsFor(product), [product]);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const valueFor = (size: string, key: MeasureKey) => {
    const value = (bodyMeasures[size] ?? bodyMeasures.M)[key];
    return unit === 'cm' ? value : (value / 2.54).toFixed(1);
  };

  return <div className="size-guide-modal" role="dialog" aria-modal="true" aria-labelledby="size-guide-title">
    <button className="page-scrim" aria-label="Fechar guia de tamanhos" onClick={onClose} />
    <section className="size-guide-panel">
      <header className="size-guide-header"><div><p className="eyebrow">Sena Beachway</p><h2 id="size-guide-title">Guia de tamanhos</h2></div><button className="icon-button" aria-label="Fechar guia de tamanhos" onClick={onClose}><X size={20} /></button></header>
      <div className="size-guide-controls"><div><strong>Alterne para</strong><span>Referência de tamanho BR</span></div><div className="unit-toggle" role="group" aria-label="Unidade de medida"><button className={unit === 'cm' ? 'is-selected' : ''} onClick={() => setUnit('cm')}>CM</button><button className={unit === 'in' ? 'is-selected' : ''} onClick={() => setUnit('in')}>IN</button></div></div>
      <div className="fit-guide" aria-label="Tipo de ajuste: regular"><strong>Tipo de ajuste</strong><div className="fit-scale"><span>Justo</span><span className="is-current">Regular</span><span>Amplo</span></div></div>
      <div className="size-guide-table-wrap"><div className="size-guide-table-heading"><h3>Medidas de referência</h3><span>em {unit === 'cm' ? 'centímetros' : 'polegadas'}</span></div><table><thead><tr><th>BR</th><th>Tamanho</th>{columns.map((column) => <th key={column}>{labels[column]}</th>)}</tr></thead><tbody>{product.sizes.map((size) => <tr key={size}><th scope="row">{size}</th><td>{sizeNames[size] ?? size}</td>{columns.map((column) => <td key={column}>{valueFor(size, column)}</td>)}</tr>)}</tbody></table></div>
      <p className="size-guide-note">Compare as medidas com uma peça sua. Por ser uma modelagem com elasticidade, pode haver variação de até 2 cm.</p>
    </section>
  </div>;
}
